import { AxiosResponse } from 'axios'
import React from 'react'
import { useParams } from 'react-router-dom'
import { isMobile } from 'react-device-detect'
import casinoService from '../../services/casino.service'
import authService from '../../services/auth.service'
import CasinoListItemInt from '../CasinoList/CasinoListItemInt'
import GameAlertBanner from '../../components/GameAlertBanner'
import { useNavigateCustom } from '../_layout/elements/custom-link'
import {
  CASINO_HUB_META,
  type CasinoHubSlug,
} from '../../constants/casinoFirstStripHubs'

type HubSection = { title: string; games: any[] }

const VALID_HUB_SLUGS = new Set<string>(Object.keys(CASINO_HUB_META))

const CasinoHubPage = () => {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigateCustom()
  const [sections, setSections] = React.useState<HubSection[]>([])
  const [loading, setLoading] = React.useState(true)
  const [searchQuery, setSearchQuery] = React.useState('')
  const [gamePlay, setGamePlay] = React.useState<any>()
  const [gameUrl, setGameUrl] = React.useState('')

  const hubSlug = slug && VALID_HUB_SLUGS.has(slug) ? (slug as CasinoHubSlug) : null
  const meta = hubSlug ? CASINO_HUB_META[hubSlug] : null

  React.useEffect(() => {
    if (!hubSlug) {
      setLoading(false)
      return
    }
    setLoading(true)
    casinoService
      .getHubCasinoGames(hubSlug)
      .then((res: AxiosResponse<any>) => {
        const body = res.data?.data ?? res.data
        const rawSections = Array.isArray(body?.sections) ? body.sections : []
        setSections(
          rawSections.map((s: HubSection) => ({
            title: s.title,
            games: Array.isArray(s.games) ? s.games : [],
          })),
        )
      })
      .catch(() => setSections([]))
      .finally(() => setLoading(false))
  }, [hubSlug])

  const filterBySearch = React.useCallback(
    (games: any[]) => {
      const q = searchQuery.trim().toLowerCase()
      if (!q) return games
      return games.filter(
        (game) =>
          (game.game_name && String(game.game_name).toLowerCase().includes(q)) ||
          (game.identifier && String(game.identifier).toLowerCase().includes(q)),
      )
    },
    [searchQuery],
  )

  React.useEffect(() => {
    if (!gamePlay) return
    const gameId = gamePlay.game_identifier || gamePlay.identifier || gamePlay._id
    if (!gameId) {
      const { toast } = require('react-toastify')
      toast.warn('This game cannot be launched. Missing game identifier.')
      setGamePlay(undefined)
      return
    }
    const payload: any = {
      gameCode: gameId,
      lobby_url: gameId,
      ipAddress: authService.getIpAddress(),
      isMobile: isMobile,
    }
    casinoService
      .getplaycasino(payload)
      .then((res: AxiosResponse<any>) => {
        setGameUrl(res.data.data.url)
      })
      .catch((error: any) => {
        const { toast } = require('react-toastify')
        let errorMessage = 'This game is currently not available, please try again later'
        try {
          const responseMsg = error.response?.data?.message || ''
          if (/currently locked/i.test(responseMsg)) {
            errorMessage = 'Betting is currently locked for this market by the administrator.'
          } else if (responseMsg.includes("game didn't start") || responseMsg.includes('Start time')) {
            errorMessage = 'This game is not available at this time. Please check the game schedule.'
          } else if (responseMsg.includes('maintenance') || responseMsg.includes('Maintenance')) {
            errorMessage = 'This game is under maintenance. Please try again later.'
          } else if (error.response?.status === 500) {
            errorMessage = 'This game is temporarily unavailable. Please try another game.'
          }
        } catch {
          // default message
        }
        toast.warn(errorMessage, { position: 'top-center', autoClose: 4000 })
        setGamePlay(undefined)
      })
  }, [gamePlay])

  if (!hubSlug || !meta) {
    return (
      <div className='container-fluid container-fluid-5 cas-in-list'>
        <div className='casino-hub-page'>
          <p className='casino-hub-page__status'>Category not found.</p>
          <button type='button' className='casino-hub-page__back' onClick={() => navigate.back()}>
            ← Back
          </button>
        </div>
      </div>
    )
  }

  const isMultiSection = sections.length > 1
  const totalGames = sections.reduce((n, s) => n + s.games.length, 0)

  return (
    <>
      <div className='container-fluid container-fluid-5 cas-in-list'>
        <div className='row row5'>
          <div className='col-12 casino-int-layout casino-hub-page' style={{ marginTop: '5px' }}>
            <header className='casino-int-banner'>
              <div className='casino-int-banner__title'>
                <button
                  type='button'
                  className='casino-hub-page__back'
                  onClick={() => navigate.back()}
                  aria-label='Go back'
                >
                  ←
                </button>
                <i className='fas fa-cube casino-int-banner__icon' aria-hidden />
                <span>{meta.title.toUpperCase()}</span>
              </div>
              <form
                className='casino-int-banner__search'
                role='search'
                onSubmit={(e) => e.preventDefault()}
              >
                <input
                  type='search'
                  className='casino-int-banner__input'
                  placeholder='Search Game...'
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label='Search games'
                />
                <button type='submit' className='casino-int-banner__search-btn' aria-label='Search'>
                  <i className='fas fa-search' aria-hidden />
                </button>
              </form>
            </header>

            {meta.subtitle && (
              <p className='casino-hub-page__subtitle'>{meta.subtitle}</p>
            )}

            {loading ? (
              <p className='casino-hub-page__status'>Loading games…</p>
            ) : totalGames === 0 ? (
              <p className='casino-hub-page__status'>No games matched this category yet.</p>
            ) : isMultiSection ? (
              sections.map((section) => {
                const filtered = filterBySearch(section.games)
                if (filtered.length === 0 && searchQuery.trim()) return null
                return (
                  <section key={section.title} className='casino-hub-page__section'>
                    <h2 className='casino-hub-page__section-title'>{section.title}</h2>
                    {filtered.length === 0 ? (
                      <p className='casino-hub-page__status'>No games in this section.</p>
                    ) : (
                      <div className='casino-list casino-int-games-grid mt-2'>
                        <CasinoListItemInt games={filtered} setGamePlay={setGamePlay} />
                      </div>
                    )}
                  </section>
                )
              })
            ) : (
              <div className='casino-list casino-int-games-grid mt-2'>
                {sections[0] && filterBySearch(sections[0].games).length === 0 ? (
                  <p className='casino-hub-page__status'>No games match your search.</p>
                ) : (
                  sections[0] && (
                    <CasinoListItemInt
                      games={filterBySearch(sections[0].games)}
                      setGamePlay={setGamePlay}
                    />
                  )
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <GameAlertBanner gamePlay={gameUrl && gamePlay ? gamePlay : null} />
      {gamePlay?.game_name && gameUrl && (
        <div className='slot-iframe show'>
          <div className='slot-header'>
            <div className='title'>
              <h4 className='mb-0' style={{ fontSize: '20px', fontWeight: '400' }}>
                {gamePlay?.game_name}
              </h4>
            </div>
            <div className='close-slot-frame' onClick={() => setGamePlay(undefined)}>
              EXIT
            </div>
          </div>
          <iframe
            scrolling='no'
            allow='fullscreen;'
            src={gameUrl}
            style={{ width: '100%', border: '0px' }}
            title={gamePlay?.game_name}
          />
        </div>
      )}
    </>
  )
}

export default CasinoHubPage
